import { style, globalStyle } from '@vanilla-extract/css'
import { vars, focusRing } from '../design/theme.css'
import {
  birdNameRow,
  gloss,
  kicker,
  reading,
  sourceMark,
  sourceMarkHover,
  utility,
} from '../design/primitives/text.css'

// The three reading blocks Live and the Journal share — the roll of birds heard, the day's
// news, and the figures line that heads them — plus the citation line under a narration.
//
// They were the last SPA components still drawn from the global editorial.css, and they showed
// it: the roll's name was 15px and its gloss 0.82em, "New & notable" was 9px/700 mono and its
// category labels 8.5px, none of which is on either type scale. Same components, same look,
// tokens now, and the roll's name matches the Journal rail's — it is the same list.

// ---- The section eyebrow shared by both blocks ------------------------------
export const tag = style([kicker, { display: 'block', marginBottom: vars.space[3] }])

// ---- New & notable ----------------------------------------------------------
// `marginInline` is deliberately NOT auto: on the Journal this block is a flex child, and auto
// margins there shrank it to its content and floated it into the middle of the page.
export const notable = style({ marginTop: vars.space[7] })
export const notableList = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.space[2],
  margin: 0,
  padding: 0,
  listStyle: 'none',
})
export const notableRow = style({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: `${vars.space[1]} ${vars.space[3]}`,
})
export const notableKind = style([utility, { flex: 'none' }])
export const notableNames = style({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: `0 ${vars.space[2]}`,
})
export const notableName = style({
  appearance: 'none',
  background: 'none',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
  color: vars.color.ink,
  selectors: {
    '&:hover': { textDecoration: 'underline', textUnderlineOffset: vars.uline.mark },
    '&:focus-visible': focusRing,
  },
})
export const notableSep = style({ color: vars.color.inkSoft })

// ---- The roll of birds heard ------------------------------------------------
export const recent = style({ marginTop: vars.space[7] })
export const list = style({ listStyle: 'none', margin: 0, padding: 0, maxWidth: vars.measure.wide })
export const row = style({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: vars.space[4],
  width: '100%',
  appearance: 'none',
  background: 'transparent',
  border: 0,
  borderBottom: `1px solid ${vars.color.rule}`,
  cursor: 'pointer',
  font: 'inherit',
  textAlign: 'left',
  color: 'inherit',
  padding: `${vars.space[2]} 0`,
  selectors: { '&:hover': { background: vars.color.paper2 }, '&:focus-visible': focusRing },
})
export const rowName = birdNameRow
export const rowGloss = style([gloss, { marginLeft: vars.space[2] }])
export const rowMeta = style([reading, { flex: '0 0 auto', fontSize: vars.ms.sm, color: vars.color.inkSoft }])

// ---- The figures line above a roll ------------------------------------------
export const figures = style([
  reading,
  {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: `${vars.space[1]} ${vars.space[3]}`,
    margin: `0 0 ${vars.space[4]}`,
    maxWidth: vars.measure.wide,
    color: vars.color.inkSoft,
  },
])
export const figuresSeg = style({ display: 'inline-flex', alignItems: 'baseline', gap: vars.space[2] })
globalStyle(`${figuresSeg} b`, { fontWeight: 500, color: vars.color.ink })
// The separator is punctuation, not a rule: at --rule it was invisible on paper (§16).
export const sep = style({ color: vars.color.inkSoft })

// ---- The citation line under a narration ------------------------------------
export const cites = style({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: `${vars.space[1]} ${vars.space[4]}`,
  margin: `${vars.space[5]} 0 0`,
  maxWidth: vars.measure.wide,
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.label,
  color: vars.color.inkSoft,
})
export const citesLabel = style([utility, { color: vars.color.ink2 }])
// The house source mark: no underline, a hairline that darkens on hover — the same treatment
// the Journal's citation tails and the species sheet's lore credits wear.
globalStyle(`${cites} a`, {
  ...sourceMark,
})
globalStyle(`${cites} a:hover`, sourceMarkHover)
globalStyle(`${cites} a:focus-visible`, focusRing)
